import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  DialogActions,
  Divider,
  Chip,
  Stack,
} from "@mui/material";

import { CloseDialogButtonIcon } from "../../../../../assets/icons/MUI/EventsIcons";
import UsersTab from "./UsersTab";
import GroupsTab from "./GroupsTab";

interface InviteModalProps {
  open: boolean;
  onClose: () => void;
  selectedUserIds: number[];
  onSelectedUserIdsChange: (userIds: number[]) => void;
  selectedGroupIds: number[];
  onSelectedGroupIdsChange: (groupIds: number[]) => void;
}

export const InviteModal = ({
  open,
  onClose,
  selectedUserIds: initialSelectedUserIds,
  onSelectedUserIdsChange,
  selectedGroupIds: initialSelectedGroupIds,
  onSelectedGroupIdsChange,
}: InviteModalProps) => {
  const { t } = useTranslation("feed", { keyPrefix: "postForm" });
  const [activeTab, setActiveTab] = useState<"users" | "groups">("users");
  const [tempSelectedUserIds, setTempSelectedUserIds] = useState<number[]>(initialSelectedUserIds);
  const [tempSelectedGroupIds, setTempSelectedGroupIds] = useState<number[]>(initialSelectedGroupIds);

  useEffect(() => {
    if (open) {
      setTempSelectedUserIds(initialSelectedUserIds);
      setTempSelectedGroupIds(initialSelectedGroupIds);
    }
  }, [open, initialSelectedUserIds, initialSelectedGroupIds]);

  const handleUserSelect = (userId: number) => {
    setTempSelectedUserIds((prev) => {
      const isSelected = prev.includes(userId);
      if (isSelected) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleGroupSelect = (groupId: number) => {
    setTempSelectedGroupIds((prev) => {
      const isSelected = prev.includes(groupId);
      if (isSelected) {
        return prev.filter((id) => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  const handleSave = () => {
    onSelectedUserIdsChange(tempSelectedUserIds);
    onSelectedGroupIdsChange(tempSelectedGroupIds);
    onClose();
  };

  const handleCancel = () => {
    setTempSelectedUserIds(initialSelectedUserIds);
    setTempSelectedGroupIds(initialSelectedGroupIds);
    onClose();
  };

  const IS_SAVE_DISABLED =
    tempSelectedUserIds.length === 0 &&
    tempSelectedGroupIds.length === 0 &&
    initialSelectedUserIds.length === 0 &&
    initialSelectedGroupIds.length === 0;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "16px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          padding: "24px 24px 20px 24px",
        }}
      >
        <Typography
          sx={{
            width: "100%",
            color: "var(--colors-text-text-primary-900, #101828)",
            textAlign: "center",
            fontFamily: "Montserrat",
            fontSize: "18px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "28px",
          }}
        >
          {t("invitePeople")}
        </Typography>
        <IconButton
          onClick={handleCancel}
          size="small"
          sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}
        >
          <CloseDialogButtonIcon />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: "#EAECF0" }} />
      <DialogContent sx={{ padding: "24px", height: "600px", overflowY: "hidden" }}>
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Chip
            label={t("people")}
            onClick={() => setActiveTab("users")}
            color={activeTab === "users" ? "primary" : "default"}
            variant={activeTab === "users" ? "filled" : "outlined"}
            sx={{
              borderRadius: "16px",
              height: "32px",
              fontSize: "14px",
              fontWeight: 500,
            }}
          />
          <Chip
            label={t("groups")}
            onClick={() => setActiveTab("groups")}
            color={activeTab === "groups" ? "primary" : "default"}
            variant={activeTab === "groups" ? "filled" : "outlined"}
            sx={{
              borderRadius: "16px",
              height: "32px",
              fontSize: "14px",
              fontWeight: 500,
            }}
          />
        </Stack>

        {activeTab === "users" && <UsersTab selectedUserIds={tempSelectedUserIds} onUserSelect={handleUserSelect} />}

        {activeTab === "groups" && (
          <GroupsTab selectedGroupIds={tempSelectedGroupIds} onGroupSelect={handleGroupSelect} />
        )}
      </DialogContent>
      <Divider sx={{ borderColor: "#EAECF0" }} />
      <DialogActions sx={{ display: "flex", justifyContent: "flex-end", gap: "12px", padding: "12px 16px" }}>
        <Button variant="outlined-md" onClick={handleCancel}>
          {t("cancel")}
        </Button>
        <Button variant="primary-md" onClick={handleSave} disabled={IS_SAVE_DISABLED}>
          {t("saveChanges")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteModal;
